import React, { FC, useEffect, useState, useMemo, startTransition } from 'react';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  CircularProgress,
  TableSortLabel,
  TextField,
  TablePagination,
  Typography,
  Chip,
  Link,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAppState } from 'src/state-management/state-provider';
import { AdminAction } from 'src/state-management/admin.action';
import { DeviceAdminDetailWithDeployment, DeviceStatus } from 'src/api/Api';
import clientService from 'src/clientService';
import { Add } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ConfirmationDialog from 'src/view/components/ConfirmationDialog';
import { useSnackbar } from 'src/view/components/SnackbarService';

type Order = 'asc' | 'desc';

interface Column {
  id: keyof DeviceAdminDetailWithDeployment | 'actions';
  label: string;
  sortable?: boolean;
  filterable?: boolean;
}

const columns: readonly Column[] = [
  { id: 'human_readable_id', label: 'Device ID', sortable: true, filterable: false },
  { id: 'deployment_id', label: 'In Use', sortable: false, filterable: false },
  { id: 'type', label: 'Type', sortable: true, filterable: false },
  { id: 'name', label: 'Name', sortable: true, filterable: false },
  { id: 'status', label: 'Status', sortable: true, filterable: false },
  { id: 'purchased_at', label: 'Purchased At', sortable: true, filterable: false },
  { id: 'serial_number', label: 'Serial Number', sortable: true, filterable: false },
  { id: 'actions', label: 'Actions', sortable: false, filterable: false },
];

const getDeviceStatusChip = (status: DeviceStatus) => {
  let color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' = 'default';
  switch (status) {
    case DeviceStatus.ACTIVE:
      color = 'success';
      break;
    case DeviceStatus.INACTIVE:
      color = 'default';
      break;
    case DeviceStatus.DEFECTIVE:
      color = 'error';
      break;
    case DeviceStatus.LOW_BATTERY:
      color = 'warning';
      break;
    case DeviceStatus.RETIRED:
      color = 'secondary';
      break;
    default:
      break;
  }
  return <Chip label={status} color={color} size="small" />;
};

const Devices: FC = () => {
  const { state, dispatch } = useAppState();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const { showSnackbar, snackbarType } = useSnackbar();

  const navigate = useNavigate();

  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof DeviceAdminDetailWithDeployment>('human_readable_id');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState<Partial<Record<keyof DeviceAdminDetailWithDeployment, string>>>({});

  useEffect(() => {
    const fetchDevices = async () => {
      if (state.admin.devices.length === 0) {
        setLoading(true);
        setError(null);
        try {
          const response = await clientService.api.getAllDevicesAdminApiV1AdminDevicesGet();
          startTransition(() => {
            dispatch({ type: 'ADMIN_SET_DEVICES', payload: response.data } as AdminAction);
          });
        } catch (err: any) {
          setError(err.message || 'Failed to fetch devices');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchDevices();
  }, [state.admin.devices.length, dispatch]);

  const handleAddDevice = () => {
    startTransition(() => {
      navigate('/admin/add-device');
    });
  };

  const handleEditDevice = (device: DeviceAdminDetailWithDeployment) => {
    startTransition(() => {
      navigate(`/admin/edit-device/${device.device_id}`);
    });
  };

  const handleDeleteDevice = (deviceId: string) => {
    setSelectedDeviceId(deviceId);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedDeviceId) {
      try {
        await clientService.api.deleteApiDeviceApiV1AdminDevicesDeviceIdDelete(selectedDeviceId);
        const updatedDevices = state.admin.devices.filter(
          (device) => device.device_id !== selectedDeviceId
        );
        dispatch({ type: 'ADMIN_SET_DEVICES', payload: updatedDevices });
        showSnackbar({ message: 'Device retired successfully', type: snackbarType.SUCCESS });
      } catch (err: any) {
        setError(err.message || 'Failed to retire device');
        showSnackbar({ message: 'Failed to retire device', type: snackbarType.ERROR });
      } finally {
        setConfirmOpen(false);
        setSelectedDeviceId(null);
      }
    }
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setSelectedDeviceId(null);
  };

  const handleRequestSort = (property: keyof DeviceAdminDetailWithDeployment) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterChange = (property: keyof DeviceAdminDetailWithDeployment, value: string) => {
    setFilters({
      ...filters,
      [property]: value,
    });
    setPage(0);
  };

  const filteredDevices = useMemo(() => {
    return state.admin.devices.filter((device: DeviceAdminDetailWithDeployment) => {
      return Object.entries(filters).every(([key, value]) => {
        const filterKey = key as keyof DeviceAdminDetailWithDeployment;
        const deviceValue = device[filterKey];

        if (deviceValue === null || deviceValue === undefined) {
          return value === '';
        }
        if (filterKey === 'type' || filterKey === 'status') {
          return String(deviceValue).toLowerCase().includes(value.toLowerCase());
        }
        return String(deviceValue).toLowerCase().includes(value.toLowerCase());
      });
    });
  }, [state.admin.devices, filters]);

  const sortedDevices = useMemo(() => {
    return [...filteredDevices].sort((a: DeviceAdminDetailWithDeployment, b: DeviceAdminDetailWithDeployment) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (aValue < bValue) {
        return order === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredDevices, order, orderBy]);

  const paginatedDevices = useMemo(() => {
    return sortedDevices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [sortedDevices, page, rowsPerPage]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" onClick={handleAddDevice} endIcon={<Add />}>
          Add Device
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Typography color="error" sx={{ mt: 2 }}>
          Error: {error}
        </Typography>
      )}

      {!loading && !error && (
        <>
          <TableContainer>
            <Table sx={{ minWidth: 650 }} aria-label="devices table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column.id}>
                      {column.sortable ? (
                        <TableSortLabel
                          active={orderBy === column.id}
                          direction={orderBy === column.id ? order : 'asc'}
                          onClick={() => handleRequestSort(column.id as keyof DeviceAdminDetailWithDeployment)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                      {column.filterable && (
                        <Box sx={{ mt: 1 }}>
                          <TextField
                            size="small"
                            variant="outlined"
                            placeholder={`Filter ${column.label}`}
                            onChange={(e) => handleFilterChange(column.id as keyof DeviceAdminDetailWithDeployment, e.target.value)}
                          />
                        </Box>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedDevices.length > 0 ? (
                  paginatedDevices.map((device: DeviceAdminDetailWithDeployment) => (
                    <TableRow
                      key={device.device_id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        {device.human_readable_id}
                      </TableCell>
                      <TableCell>{device.deployment_id ? (<>
                        <Link
                          underline="hover"
                          color="warning"
                          onClick={() => {
                            navigate(`/admin/deployment/${device.deployment_id}/upload`);
                          }}
                          sx={{ cursor: 'pointer' }}
                        >
                          View Deployment
                        </Link>
                      </>) : <Chip label="Available" color='info'></Chip>}</TableCell>
                      <TableCell>{device.type}</TableCell>
                      <TableCell>{device.name}</TableCell>
                      <TableCell>{getDeviceStatusChip(device.status)}</TableCell>
                      <TableCell>{device.purchased_at ? new Date(device.purchased_at).toLocaleDateString() : 'N/A'}</TableCell>
                      <TableCell>{device.serial_number}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          aria-label="edit"
                          onClick={() => handleEditDevice(device)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          aria-label="delete"
                          onClick={() => handleDeleteDevice(device.device_id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      No devices found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredDevices.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}
      <ConfirmationDialog
        open={confirmOpen}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        title="Retire Device"
        content="This will not delete the device but will retire it. Once retired, it cannot be used for any deployment. The device can have multiple statuses: ACTIVE, INACTIVE, DEFECTIVE, LOW_BATTERY, RETIRED."
        confirmButtonText="Retire"
      />
    </Paper>
  );
};

export default Devices;
